import { api } from "./client";
import type { HeldReasonCode } from "./commissions";

/**
 * One loan's frozen commission attribution — L7/U7's read and correction
 * surface.
 *
 * <p>🔴 Every value arrives beside its SOURCE, and the surface renders both.
 * The row is read verbatim for the life of the loan, so an ADMIN deciding
 * whether to change one is deciding whether to overrule the assignment ledger,
 * a backfill's inference, or an earlier correction — and those are three
 * different decisions.</p>
 */
export interface LoanCommissionAttributionDto {
  loanId: string;
  loanNumber?: string | null;
  /** Whether a row exists at all. `false` is the `attribution not stamped`
   *  population, which this surface exists to repair — never an error. */
  isStamped: boolean;
  buyerLeadId?: string | null;
  buyerLeadIdSource?: string | null;
  attributedSpecialistUserId?: string | null;
  attributedSpecialistName?: string | null;
  attributedSpecialistSource?: string | null;
  attributedSpecialistTeamId?: string | null;
  attributedSpecialistTeamName?: string | null;
  teamSource?: string | null;
  teamF1UserId?: string | null;
  teamF1Name?: string | null;
  teamF1Source?: string | null;
  brokerLiaisonUserId?: string | null;
  brokerLiaisonName?: string | null;
  brokerLiaisonSource?: string | null;
  brokerSideF1UserId?: string | null;
  brokerSideF1Name?: string | null;
  brokerSideF1Source?: string | null;
  /** `YYYY-MM-DD` — a wall-calendar day, never an instant. */
  brokerSideF1EffectiveFrom?: string | null;
  heldReason?: HeldReasonCode | null;
  heldReasonDetail?: string | null;
  stampedAt?: string | null;
  corrections: LoanCommissionAttributionCorrectionDto[];
}

export interface LoanCommissionAttributionCorrectionDto {
  id: string;
  reason: string;
  attestedByUserId?: string | null;
  attestedByName?: string | null;
  attestedAt: string;
}

/**
 * ⚠️ A FULL REPLACE, NOT A PATCH. A null field means null; it does not mean
 * "leave it alone". The form hydrates every value before offering it for
 * exactly that reason, and the attestation records the whole before and after.
 */
export interface CorrectLoanCommissionAttributionRequest {
  reason: string;
  buyerLeadId?: string | null;
  attributedSpecialistUserId?: string | null;
  attributedSpecialistTeamId?: string | null;
  teamF1UserId?: string | null;
  brokerLiaisonUserId?: string | null;
  brokerSideF1UserId?: string | null;
  brokerSideF1EffectiveFrom?: string | null;
}

export interface LoanCommissionAttributionCorrectionResultDto {
  loanId: string;
  correctionId: string;
  claimsMoved: number;
  rollupPeriodsRedrafted: number;
  /** 🔴 Read this one. Those months keep the OLD apportionment until somebody
   *  unlocks the accounting period — the correction did not reach them. */
  rollupPeriodsLocked: number;
}

export const loanCommissionAttributionsApi = {
  get: (loanId: string) =>
    api.get<LoanCommissionAttributionDto>(`/commissions/loan-attributions/${loanId}`),
  correct: (loanId: string, body: CorrectLoanCommissionAttributionRequest) =>
    api.post<LoanCommissionAttributionCorrectionResultDto>(
      `/commissions/loan-attributions/${loanId}/corrections`,
      body,
    ),
};
